/**
 * WebAuthn helpers — base64url encode/decode + register/login flows.
 */
import { api } from "./api";

const b64u = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
  .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const un_b64u = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

export const isWebAuthnSupported = () =>
  !!(window.PublicKeyCredential && navigator.credentials);

export async function registerPasskey() {
  const { data: opts } = await api.post("/webauthn/register/options");
  const publicKey = {
    ...opts,
    challenge: un_b64u(opts.challenge),
    user: { ...opts.user, id: un_b64u(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({ ...c, id: un_b64u(c.id) })),
  };
  const cred = await navigator.credentials.create({ publicKey });
  const payload = {
    id: cred.id,
    rawId: b64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: b64u(cred.response.clientDataJSON),
      attestationObject: b64u(cred.response.attestationObject),
    },
  };
  await api.post("/webauthn/register/verify", { credential: payload });
  return true;
}

export async function loginWithPasskey(email) {
  const { data: opts } = await api.post("/webauthn/login/options", { email });
  const publicKey = {
    ...opts,
    challenge: un_b64u(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({ ...c, id: un_b64u(c.id) })),
  };
  const cred = await navigator.credentials.get({ publicKey });
  const payload = {
    id: cred.id,
    rawId: b64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: b64u(cred.response.clientDataJSON),
      authenticatorData: b64u(cred.response.authenticatorData),
      signature: b64u(cred.response.signature),
      userHandle: cred.response.userHandle ? b64u(cred.response.userHandle) : null,
    },
  };
  const { data } = await api.post("/webauthn/login/verify", { email, credential: payload });
  return data; // { token, user }
}

export async function hasPasskey(email) {
  const { data } = await api.get(`/webauthn/available?email=${encodeURIComponent(email)}`);
  return !!data.has_passkey;
}
